import React, { useState, useMemo, useCallback } from 'react';
import { runApiAnalysisIvCompare } from '../../services/api';
import { IV_REASON_LABELS } from '../../utils/dataSourceTooltip';

// Shared with the IV hover tooltips (utils/dataSourceTooltip.js).
export const REASON_LABELS = IV_REASON_LABELS;

const BUCKET_OPTIONS = [
  { days: 30, label: '1M' },
  { days: 90, label: '3M' },
  { days: 180, label: '6M' },
  { days: 365, label: '12M' },
];

const STORAGE_PREFIX = 'ivCompare:robinhood:';

function todayKey() {
  return STORAGE_PREFIX + new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function loadEntries() {
  try {
    return JSON.parse(localStorage.getItem(todayKey()) || '{}') || {};
  } catch (e) {
    return {};
  }
}

function saveEntries(entries) {
  try {
    localStorage.setItem(todayKey(), JSON.stringify(entries));
  } catch (e) {
    // Storage blocked (private window etc.): entries just won't survive a refresh.
  }
}

const pct = (v) => (v == null ? '–' : `${(v * 100).toFixed(1)}%`);
const num = (v, d = 2) => (v == null ? '–' : Number(v).toFixed(d));
const signed = (v, d = 2, suffix = '') => (v == null ? '–' : `${v > 0 ? '+' : ''}${Number(v).toFixed(d)}${suffix}`);

function quoteTime(ms) {
  if (!ms) return '–';
  return `${new Date(ms).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
  })} ET`;
}

function gapTone(v) {
  if (v == null) return '';
  const a = Math.abs(v);
  if (a < 1) return 'good';
  if (a < 3) return 'warn';
  return 'bad';
}

function errorsVs(row, rhPct) {
  if (rhPct == null) return null;
  const m = row.massive_iv != null ? Math.abs(row.massive_iv * 100 - rhPct) : null;
  const a = row.app_iv != null ? Math.abs(row.app_iv * 100 - rhPct) : null;
  let closer = null;
  if (m != null && a != null) closer = Math.abs(m - a) < 0.05 ? 'tie' : (m < a ? 'massive' : 'app');
  else if (m != null) closer = 'massive';
  else if (a != null) closer = 'app';
  return { m, a, closer };
}

function mean(values) {
  const v = values.filter((x) => x != null && Number.isFinite(x));
  return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
}

function ReportsIvCompare() {
  const [tickersText, setTickersText] = useState('MSFT, AAPL, GOOG');
  const [buckets, setBuckets] = useState([30, 90, 180, 365]);
  const [types, setTypes] = useState('call');
  const [strikes, setStrikes] = useState('atm');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [entries, setEntries] = useState(loadEntries);
  const [copied, setCopied] = useState('');

  const run = async (e) => {
    e.preventDefault();
    const tickers = tickersText.split(/[,\s]+/).map((t) => t.trim().toUpperCase()).filter(Boolean);
    if (!tickers.length || !buckets.length) return;
    setRunning(true);
    setError(null);
    try {
      setResult(await runApiAnalysisIvCompare({ tickers, buckets, types, strikes }));
      setEntries(loadEntries());
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'IV comparison failed');
    } finally {
      setRunning(false);
    }
  };

  const setEntry = useCallback((contract, value) => {
    setEntries((prev) => {
      const next = { ...prev };
      if (value === '') delete next[contract];
      else next[contract] = value;
      saveEntries(next);
      return next;
    });
  }, []);

  const toggleBucket = (days) => {
    setBuckets((prev) => (prev.includes(days) ? prev.filter((d) => d !== days) : [...prev, days].sort((a, b) => a - b)));
  };

  const rows = useMemo(() => result?.rows || [], [result]);

  const summary = useMemo(() => {
    const scored = rows
      .map((r) => {
        const rh = parseFloat(entries[r.ticker]);
        return Number.isFinite(rh) ? errorsVs(r, rh) : null;
      })
      .filter(Boolean);
    return {
      entered: scored.length,
      massive: scored.filter((s) => s.closer === 'massive').length,
      app: scored.filter((s) => s.closer === 'app').length,
      tie: scored.filter((s) => s.closer === 'tie').length,
      maeMassive: mean(scored.map((s) => s.m)),
      maeApp: mean(scored.map((s) => s.a)),
      gapMassive: mean(rows.map((r) => (r.reprice_gap_massive_pct == null ? null : Math.abs(r.reprice_gap_massive_pct)))),
      gapApp: mean(rows.map((r) => (r.reprice_gap_app_pct == null ? null : Math.abs(r.reprice_gap_app_pct)))),
    };
  }, [rows, entries]);

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 1500);
    } catch (e) {
      setCopied('Copy blocked by the browser');
    }
  };

  const csv = () => {
    const head = ['contract', 'occ', 'bucket', 'dte', 'underlying', 'moneyness_pct', 'bid', 'ask', 'mid',
      'timeframe', 'massive_iv_pct', 'app_iv_pct', 'diff_pts', 'robinhood_iv_pct', 'massive_delta', 'app_delta',
      'reprice_gap_massive_pct', 'reprice_gap_app_pct', 'app_iv_reason'];
    const lines = rows.map((r) => [
      `"${r.label}"`, r.ticker, r.bucket, r.dte, r.underlying_price, r.moneyness_pct, r.bid, r.ask, r.mid, r.timeframe,
      r.massive_iv != null ? (r.massive_iv * 100).toFixed(2) : '', r.app_iv != null ? (r.app_iv * 100).toFixed(2) : '',
      r.diff_pts ?? '', entries[r.ticker] ?? '', r.massive_delta ?? '', r.app_delta ?? '',
      r.reprice_gap_massive_pct ?? '', r.reprice_gap_app_pct ?? '', r.app_iv_reason ?? '',
    ].join(','));
    return [head.join(','), ...lines].join('\n');
  };

  return (
    <div className="api-analysis-ivcheck iv-compare">
      <h4 className="iv-compare-title">IV accuracy check: Massive vs App vs your broker</h4>
      <p className="api-analysis-muted">
        For each ticker and expiry bucket this picks the standard monthly expiration closest to the target and the
        strike nearest the stock price. <strong>Massive IV</strong> is what the feed sends; <strong>App IV</strong> is
        solved by the app from the same quote (European Black-Scholes, r = {result ? `${result.risk_free_rate * 100}%` : '5%'},
        no dividends). Type the IV your broker shows into the Robinhood column; entries are kept for today in this browser.
      </p>

      <form className="iv-compare-controls" onSubmit={run}>
        <label>
          Tickers
          <input value={tickersText} onChange={(e) => setTickersText(e.target.value.toUpperCase())} />
        </label>
        <div className="iv-compare-buckets">
          {BUCKET_OPTIONS.map((b) => (
            <label key={b.days} className="api-analysis-check">
              <input type="checkbox" checked={buckets.includes(b.days)} onChange={() => toggleBucket(b.days)} />
              {b.label}
            </label>
          ))}
        </div>
        <select className="period-select" value={types} onChange={(e) => setTypes(e.target.value)}>
          <option value="call">Calls</option>
          <option value="put">Puts</option>
          <option value="both">Calls + Puts</option>
        </select>
        <select className="period-select" value={strikes} onChange={(e) => setStrikes(e.target.value)}>
          <option value="atm">ATM strike</option>
          <option value="atm5">ATM and ±5%</option>
          <option value="atm10">ATM and ±10%</option>
        </select>
        <button type="submit" className="refresh-btn" disabled={running}>
          {running ? 'Fetching…' : 'Run comparison'}
        </button>
      </form>

      {error && <div className="reports-error"><p>{error}</p></div>}

      {result && (
        <>
          <div className="api-analysis-ivcheck-meta">
            Checked {result.checked_at_et} · market {result.market_open ? 'open' : 'closed'} · {rows.length} contracts.
            IV moves through the day: compare with your broker within a few minutes of this time.
            {!result.market_open && ' With the market closed, quotes are frozen and broker IVs may differ more.'}
          </div>

          <div className="api-analysis-stat-row">
            <div className="api-analysis-stat">
              <div className="kpi-label">Closer to Robinhood</div>
              <div className="api-analysis-stat-value">
                {summary.entered ? `M ${summary.massive} · A ${summary.app}` : '–'}
              </div>
              <div className="kpi-sublabel">
                {summary.entered ? `${summary.entered} entered${summary.tie ? `, ${summary.tie} tie` : ''}` : 'Enter broker IVs below'}
              </div>
            </div>
            <div className="api-analysis-stat">
              <div className="kpi-label">Avg error vs Robinhood</div>
              <div className="api-analysis-stat-value">
                {summary.entered ? `${num(summary.maeMassive, 2)} / ${num(summary.maeApp, 2)}` : '–'}
              </div>
              <div className="kpi-sublabel">vol points · Massive / App</div>
            </div>
            <div className="api-analysis-stat">
              <div className="kpi-label">Avg 0%-move reprice gap</div>
              <div className="api-analysis-stat-value">
                {`${num(summary.gapMassive, 2)}% / ${num(summary.gapApp, 2)}%`}
              </div>
              <div className="kpi-sublabel">heatmap bias · Massive IV / App IV</div>
            </div>
          </div>

          <div className="iv-compare-actions">
            <button type="button" className="refresh-btn" onClick={() => copy(csv(), 'CSV copied')} disabled={!rows.length}>
              Copy as CSV
            </button>
            {copied && <span className="api-analysis-hint">{copied}</span>}
          </div>

          <div className="api-analysis-table-wrap">
            <table className="api-analysis-table iv-compare-table">
              <thead>
                <tr>
                  <th>Contract</th>
                  <th>Bucket</th>
                  <th>Underlying</th>
                  <th>Bid / Ask / Mid</th>
                  <th>Massive IV</th>
                  <th>App IV</th>
                  <th>Diff</th>
                  <th>Robinhood IV</th>
                  <th>Closer</th>
                  <th>Delta M / A</th>
                  <th title="App simulator price at no move, today, vs the current premium">Reprice gap M / A</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const rh = parseFloat(entries[r.ticker]);
                  const scored = Number.isFinite(rh) ? errorsVs(r, rh) : null;
                  return (
                    <tr key={r.ticker}>
                      <td>
                        <div className="api-analysis-title">{r.label}</div>
                        <button type="button" className="iv-compare-copy" onClick={() => copy(r.ticker, `${r.ticker} copied`)}
                          title="Copy the OCC contract ticker">
                          {r.ticker}
                        </button>
                        {!r.is_monthly && <div className="api-analysis-note">weekly expiration</div>}
                      </td>
                      <td>{r.bucket}<div className="api-analysis-muted">{r.dte}d</div></td>
                      <td>
                        ${num(r.underlying_price)}
                        <div className="api-analysis-muted">{signed(r.moneyness_pct, 1, '%')} {r.moneyness_pct > 0 ? 'above' : 'below'}</div>
                      </td>
                      <td>
                        {num(r.bid)} / {num(r.ask)} / <strong>{num(r.mid)}</strong>
                        <div className="api-analysis-muted">
                          spread {num(r.spread_pct, 1)}% · {quoteTime(r.quote_time_ms)}
                        </div>
                        {r.timeframe && (
                          <span className={`api-analysis-chip ${r.timeframe === 'REAL-TIME' ? 'good' : 'warn'}`}>{r.timeframe}</span>
                        )}
                      </td>
                      <td className="iv-compare-iv">{pct(r.massive_iv)}</td>
                      <td className="iv-compare-iv">
                        {r.app_iv != null ? pct(r.app_iv) : (
                          <span className="api-analysis-note">{REASON_LABELS[r.app_iv_reason] || r.app_iv_reason || 'unavailable'}</span>
                        )}
                      </td>
                      <td>{r.diff_pts != null ? `${signed(r.diff_pts, 2)} pts` : '–'}</td>
                      <td>
                        <input
                          className="iv-compare-input"
                          inputMode="decimal"
                          placeholder="e.g. 31.2"
                          value={entries[r.ticker] ?? ''}
                          onChange={(e) => setEntry(r.ticker, e.target.value.replace(/[^0-9.]/g, ''))}
                        />
                        <span className="api-analysis-muted"> %</span>
                      </td>
                      <td>
                        {scored ? (
                          <>
                            {scored.closer === 'massive' && <span className="api-analysis-chip good">Massive</span>}
                            {scored.closer === 'app' && <span className="api-analysis-chip good">App</span>}
                            {scored.closer === 'tie' && <span className="api-analysis-chip muted">Tie</span>}
                            <div className="api-analysis-muted">
                              M {num(scored.m, 2)} · A {num(scored.a, 2)} pts
                            </div>
                          </>
                        ) : '–'}
                      </td>
                      <td>{num(r.massive_delta, 3)} / {num(r.app_delta, 3)}</td>
                      <td>
                        <span className={`iv-compare-gap ${gapTone(r.reprice_gap_massive_pct)}`}>
                          {signed(r.reprice_gap_massive_pct, 2, '%')}
                        </span>
                        {' / '}
                        <span className={`iv-compare-gap ${gapTone(r.reprice_gap_app_pct)}`}>
                          {signed(r.reprice_gap_app_pct, 2, '%')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {result.errors?.length > 0 && (
            <ul className="api-analysis-list iv-compare-errors">
              {result.errors.map((e) => (
                <li key={`${e.ticker}-${e.bucket}`}>{e.ticker}{e.bucket ? ` ${e.bucket}` : ''}: {e.error}</li>
              ))}
            </ul>
          )}

          <p className="api-analysis-muted">
            <strong>Reprice gap</strong>: the app simulator&apos;s price for this contract with the stock unchanged and no
            time passed, minus the current premium. It is the bias every heatmap cell starts from with that IV;
            App IV is solved inside the simulator, so its gap is ~0% unless the quote was paired with an anchored
            (earlier) stock price.
          </p>
        </>
      )}
    </div>
  );
}

export default ReportsIvCompare;
